import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetCustomerLocationListQuery } from 'api/accounts/getCustomerLocationList'
import { useGetAllConfiguredServicesQuery } from 'api/configured-service/searchConfiguredServices'

import {
  groupReactivatableByLocation,
  selectAllReactivatable,
  emptySelection,
  toggleLocation,
  toggleService,
} from './reactivationSelection'

import type { ReactivationLocationGroup, ReactivationLocationRow, ReactivationServiceRow } from './reactivationSelection'

type ReactivationSelectionArgs = {
  accountId: string
  isOpen: boolean
}

const NO_LOCATIONS: ReactivationLocationRow[] = []
const NO_SERVICES: ReactivationServiceRow[] = []

/**
 * The account's reactivatable locations and services, grouped, with what the user has ticked: everything
 * starts selected once both lists are in, a refetch while open keeps the unticks, closing resets it.
 */
const useReactivationSelection = ({ accountId, isOpen }: ReactivationSelectionArgs) => {
  const {
    currentData: locationData,
    isFetching: isFetchingLocations,
    isError: hasLocationsError,
  } = useGetCustomerLocationListQuery({ accountId }, { skip: !isOpen, refetchOnMountOrArgChange: true })
  const {
    currentData: serviceData,
    isFetching: isFetchingServices,
    isError: hasServicesError,
  } = useGetAllConfiguredServicesQuery({ accountId }, { skip: !isOpen, refetchOnMountOrArgChange: true })

  const groups = useMemo(
    () =>
      groupReactivatableByLocation(
        (locationData ?? NO_LOCATIONS) as ReactivationLocationRow[],
        (serviceData ?? NO_SERVICES) as ReactivationServiceRow[]
      ),
    [locationData, serviceData]
  )
  const hasSelectionError = hasLocationsError || hasServicesError
  const isSelectionReady = Boolean(locationData && serviceData) && !isFetchingLocations && !isFetchingServices
  const isSelectionLoading = isOpen && !hasSelectionError && !isSelectionReady
  const [selection, setSelection] = useState(emptySelection)
  const hasSeededSelection = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      setSelection(emptySelection())
      hasSeededSelection.current = false
    }
  }, [isOpen])

  useEffect(() => {
    if (!isSelectionReady || hasSeededSelection.current) return
    hasSeededSelection.current = true
    setSelection(selectAllReactivatable(groups))
  }, [isSelectionReady, groups])

  const handleToggleLocation = useCallback(
    (group: ReactivationLocationGroup) => setSelection(current => toggleLocation(current, group)),
    []
  )
  const handleToggleService = useCallback(
    (group: ReactivationLocationGroup, serviceId: string) => setSelection(current => toggleService(current, group, serviceId)),
    []
  )
  const hasSelection = selection.locationIds.size > 0 || selection.serviceIds.size > 0
  const handleToggleAll = useCallback(
    () => setSelection(hasSelection ? emptySelection() : selectAllReactivatable(groups)),
    [hasSelection, groups]
  )

  return {
    groups,
    selection,
    hasSelection,
    isSelectionReady,
    isSelectionLoading,
    hasSelectionError,
    handleToggleLocation,
    handleToggleService,
    handleToggleAll,
  }
}

export default useReactivationSelection
